import { Mapper } from './typesUtils'

/**
 * Représente un objet en cours de chargement
 */
export type Loading = { loading: true }

/**
 * Représente un objet dont le chargement a échoué
 */
export type LoadError<ErrorReason = string> = { loading: false, error: true, reason: ErrorReason }

/**
 * Représente un objet chargé
 */
export type Loaded<T> = { loading: false, error: false, value: T }

/**
 * Représente une valeur qui peut être chargé et dont le chargement peut renvoyer des erreurs. Utilise le Type Narrowing de typescript pour nous
 * forcer à gérer les càs d'erreur et de chargement, mais également pour nous permettre d'accéder normalement à la variable une fois qu'ils sont traités.
 */
export type LoadableValue<T, ErrorReason = string> = Loading | LoadError<ErrorReason> | Loaded<T>

/**
 * Valeur qui représente un objet en train de charger
 */
export const LOADING: Loading = { loading: true }

/**
 * Méthode qui permet de créer un objet LoadError (compatible LoadableValue) à partir d'une erreur.
 * @param reason
 */
export const getErrorAsLoadable = <ErrorReason, >(reason: ErrorReason): LoadError<ErrorReason> => ({ loading: false, error: true, reason })

/**
 * Méthode qui permet de créer un objet Loaded (compatible LoadableValue) à partir d'une valeur.
 * @param value
 */
export const getValueAsLoadable = <T, >(value: T): Loaded<T> => ({ loading: false, error: false, value })

/**
 * Méthode qui permet de transformer le type d'un objet LoadableValue en un autre
 * @param loadable
 * @param mapper
 */
export const mapLoadable = <T, U>(loadable: LoadableValue<T>, mapper: Mapper<T, U>): LoadableValue<U> => {
	if (loadable.loading) {
		return LOADING
	}
	if (loadable.error) {
		return loadable
	}
	return getValueAsLoadable(mapper(loadable.value))
}

/**
 * Permet de récupérer la valeur chargée si elle existe ou bien une valeur par défault
 * @param loadable
 * @param defaultValue
 */
export const getLoadedValueOrElse = <T,>(loadable: LoadableValue<T>, defaultValue: T): T => {
	if (loadable.loading || loadable.error) {
		return defaultValue
	}
	return loadable.value
}